import { TFunction } from "@impero-com/common/utils/i18n";

import {
    FrictionBreakdown,
    FrictionBreakdownCounts,
} from "api/generated/model";

import { buildBucketLabels, formatBucketTooltip } from "./activityTrendLabels";

export type FrictionBreakdownView = "all" | keyof FrictionBreakdownCounts;

interface FrictionStateCounts {
    inProgress: number;
    completed: number;
}

interface FrictionChartBucket<Values> {
    start: string;
    end: string;
    label: string;
    span: string;
    values: Values;
}

interface FrictionBreakdownDataBase {
    total: number;
    isEmpty: boolean;
    range: string;
}

export type FrictionBreakdownData = FrictionBreakdownDataBase &
    (
        | {
              view: "all";
              totals: FrictionBreakdownCounts;
              buckets: FrictionChartBucket<FrictionBreakdownCounts>[];
          }
        | {
              view: keyof FrictionBreakdownCounts;
              totals: FrictionStateCounts;
              buckets: FrictionChartBucket<FrictionStateCounts>[];
          }
    );

export function prepareFrictionBreakdownData(
    breakdown: FrictionBreakdown,
    view: FrictionBreakdownView,
    range: string,
    t: TFunction
): FrictionBreakdownData {
    const labels = buildBucketLabels(breakdown.buckets, breakdown.granularity);
    const buckets = breakdown.buckets.map((bucket, index) => ({
        start: bucket.start,
        end: bucket.end,
        label: labels[index],
        span: formatBucketTooltip(bucket, breakdown.granularity, t),
    }));
    const total =
        view === "all"
            ? breakdown.totals.overdue +
              breakdown.totals.rejected +
              breakdown.totals.reopened
            : breakdown.totals[view];
    const common = { total, isEmpty: total === 0, range };

    if (view === "all") {
        return {
            ...common,
            view,
            totals: { ...breakdown.totals },
            buckets: buckets.map((bucket, index) => ({
                ...bucket,
                values: { ...breakdown.buckets[index].totals },
            })),
        };
    }

    return {
        ...common,
        view,
        totals: {
            inProgress: breakdown.inProgress[view],
            completed: breakdown.completed[view],
        },
        buckets: buckets.map((bucket, index) => ({
            ...bucket,
            values: {
                inProgress: breakdown.buckets[index].inProgress[view],
                completed: breakdown.buckets[index].completed[view],
            },
        })),
    };
}
